<script setup>
import { ref, onMounted, watch } from 'vue'
import AnalyticsService from '@/services/analytics'
import AgentService from '@/services/agent'
// You might need to import specific Chart components or use a library wrapper
// import { Doughnut } from 'vue-chartjs'
// For now, assuming we might need to create Chart wrappers or use existing ones.
// Reusing RoleDistributionChart for now if it's generic, or we will modify later.
import RoleDistributionChart from './charts/RoleDistributionChart.vue' 
import { useCompanyStore } from "@/stores/company";

const companyStore = useCompanyStore();

// -- State --
const isLoading = ref(false)
const dateRange = ref(null)
const activeAgentUUID = ref(null)
const agents = ref([])

// Data
const inStockData = ref(null)
const outStockData = ref(null)
const mapsData = ref([])
const typeDistributionData = ref(null)
const stockDistributionData = ref(null)

// -- Helpers --
const toggleAgentFilter = (agentId) => {
    if (activeAgentUUID.value === agentId) {
        activeAgentUUID.value = null
    } else {
        activeAgentUUID.value = agentId
    }
}

// -- Fetch Data --
const fetchAgents = async () => {
    try {
        const currentCompanyId = companyStore.companyId
        if (currentCompanyId) {
            const response = await AgentService.getAgentsGrid(currentCompanyId)
            if (response && response.data) {
                agents.value = response.data.map((agent, index) => ({
                    id: agent.id,
                    name: agent.name,
                    status: agent.status,
                    color: index % 2 === 0 ? 'bg-primary' : 'bg-success'
                }))
            }
        }
    } catch (error) {
        console.error('Error fetching agents:', error)
    }
}

const fetchAllData = async () => {
    try {
        isLoading.value = true
        const currentCompanyId = companyStore.companyId
        if (!currentCompanyId) return

        const params = {}

        if (dateRange.value && dateRange.value[0] && dateRange.value[1]) {
            params.from_date = dateRange.value[0].toISOString()
            params.until_date = dateRange.value[1].toISOString()
        }
        
        if (activeAgentUUID.value) {
            params.agent_uuid = activeAgentUUID.value
        }

        // Reset data
        inStockData.value = null
        outStockData.value = null
        mapsData.value = []
        typeDistributionData.value = null
        stockDistributionData.value = null

        const [inStockRes, outStockRes, mapsRes, typeRes, stockRes] = await Promise.all([
            AnalyticsService.getAttachmentsInStockCards(currentCompanyId, params),
            AnalyticsService.getAttachmentsOutStockCards(currentCompanyId, params),
            AnalyticsService.getAttachmentsMaps(currentCompanyId, params),
            AnalyticsService.getAttachmentsTypeDistribution(currentCompanyId, params),
            AnalyticsService.getAttachmentsStockStatus(currentCompanyId, params)
        ])

        // 1. In/Out Stock Cards
        if (inStockRes) inStockData.value = inStockRes
        if (outStockRes) outStockData.value = outStockRes

        // 2. Maps
        if (mapsRes && mapsRes.items) mapsData.value = mapsRes.items

        // 3. Type Distribution
        if (typeRes && typeRes.distribution && typeRes.distribution.length > 0) {
            const labels = typeRes.distribution.map(d => d.attachment_type)
            const data = typeRes.distribution.map(d => d.count)
            const percentages = typeRes.distribution.map(d => d.percentage)
            const colors = ['#4285F4', '#34A853', '#EA4335', '#FBBC05', '#AA00FF']
            
            typeDistributionData.value = {
                labels: labels,
                datasets: [{
                    label: 'Type Distribution',
                    data: data,
                    backgroundColor: colors.slice(0, labels.length),
                    percentages: percentages
                }]
            }
        }

        // 4. Stock Prediction/Status Distribution (Assuming structure)
        if (stockRes && stockRes.distribution && stockRes.distribution.length > 0) {
            const labels = stockRes.distribution.map(d => d.stock_status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
            const data = stockRes.distribution.map(d => d.count)
            const percentages = stockRes.distribution.map(d => d.percentage)
            const colors = ['#34A853', '#EA4335', '#FBBC05']

            stockDistributionData.value = {
                labels: labels,
                datasets: [{
                    label: 'Stock Status',
                    data: data,
                    backgroundColor: colors.slice(0, labels.length),
                    percentages: percentages
                }]
            }
        }

    } catch (error) {
        console.error('Error fetching attachments analytics:', error)
    } finally {
        isLoading.value = false
    }
}

watch([dateRange, activeAgentUUID], () => {
    fetchAllData()
})

onMounted(() => {
    fetchAgents()
    fetchAllData()
})
</script>

<template>
  <div class="w-full p-4 dashboard-container">
    <!-- Header -->
    <div class="flex justify-between items-start mb-4 header-row">
      <div class="flex flex-col gap-1">
        <h2 class="h4 font-bold text-white mb-0 flex items-center">
          <span class="mr-2 chart-icon">📎</span> Attachments Analytics
        </h2>
        <p class="header-subtitle">Product attachment distribution by type, stock status, and wayfinding maps.</p>
      </div>
      <div class="flex gap-2">
         <div style="min-width: 250px;">
            <VueDatePicker v-model="dateRange" range :multi-calendars="{ solo: true }" placeholder="Select Date Range" :enable-time-picker="false" auto-apply class="date-picker-custom" />
         </div>
      </div>
    </div>

    <div class="grid grid-cols-12 gap-3">
        <!-- Main Content -->
        <div class="col-span-12 md:col-span-9">
            <!-- Summary Stats (Stock Cards) -->
            <div class="grid grid-cols-12 gap-3 mb-4">
              <div class="col-span-12 md:col-span-6">
                <div class="card h-full border-0 shadow-sm rounded-3">
                  <div class="card-body">
                     <h6 class="card-subtitle mb-2 text-gray-500 small">In Stock</h6>
                     <h2 class="card-title font-bold mb-1 text-success">
                         <span v-if="isLoading" class="spinner-sm" role="status" aria-hidden="true"></span>
                         <span v-else>{{ inStockData?.total_items ?? inStockData?.items?.length ?? 0 }}</span>
                     </h2>
                     <small class="text-gray-500">Items available</small>
                  </div>
                </div>
              </div>
              <div class="col-span-12 md:col-span-6">
                <div class="card h-full border-0 shadow-sm rounded-3">
                  <div class="card-body">
                     <h6 class="card-subtitle mb-2 text-gray-500 small">Out of Stock</h6>
                     <h2 class="card-title font-bold mb-1 text-danger">
                         <span v-if="isLoading" class="spinner-sm" role="status" aria-hidden="true"></span>
                         <span v-else>{{ outStockData?.total_items ?? outStockData?.items?.length ?? 0 }}</span>
                     </h2>
                     <small class="text-gray-500">Items unavailable</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Charts Row -->
            <div class="grid grid-cols-12 gap-3 mb-4">
                <div class="col-span-12 md:col-span-6">
                    <div class="card border-0 shadow-sm rounded-3 h-full">
                        <div class="card-body">
                             <h5 class="card-title font-bold mb-3">Type Distribution</h5>
                             <div style="height: 250px;">
                                 <RoleDistributionChart v-if="typeDistributionData" :data="typeDistributionData" centerLabel="Types" />
                                 <div v-else class="h-full flex items-center justify-center text-gray-500">
                                    {{ isLoading ? 'Loading...' : 'No data available' }}
                                 </div>
                             </div>
                        </div>
                    </div>
                </div>
                <div class="col-span-12 md:col-span-6">
                    <div class="card border-0 shadow-sm rounded-3 h-full">
                        <div class="card-body">
                             <h5 class="card-title font-bold mb-3">Stock Status Distribution</h5>
                             <div style="height: 250px;">
                                 <RoleDistributionChart v-if="stockDistributionData" :data="stockDistributionData" centerLabel="Status" />
                                 <div v-else class="h-full flex items-center justify-center text-gray-500">
                                    {{ isLoading ? 'Loading...' : 'No data available' }}
                                 </div>
                             </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Maps List -->
            <div class="card border-0 shadow-sm rounded-3">
                <div class="card-body">
                    <h5 class="card-title font-bold mb-3">Maps</h5>
                    <div class="table-responsive">
                        <table class="table table-hover align-middle">
                            <thead class="table-light">
                                <tr>
                                    <th>Title</th>
                                    <th>Count</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(map, index) in mapsData" :key="map.title || index">
                                    <td class="font-bold">{{ map.title?.replace(/\*\*/g, '') }}</td>
                                    <td>{{ map.attachment_count }}</td>
                                </tr>
                                <tr v-if="mapsData.length === 0">
                                    <td colspan="2" class="text-center text-gray-500 py-4">
                                        {{ isLoading ? 'Loading...' : 'No maps found' }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Right Column: Agent Filter -->
        <div class="col-span-12 md:col-span-3">
             <div class="card border-0 shadow-sm rounded-3 h-full">
                   <div class="card-body">
                       <h5 class="card-title font-bold mb-1">Filter by Agent</h5>
                       <p class="text-gray-500 small mb-3" v-if="!activeAgentUUID">Select an agent to filter data</p>
                       <p class="text-primary small mb-3 cursor-pointer" v-else @click="activeAgentUUID = null">
                           Clear filter ✕
                       </p>
                       
                       <div class="flex flex-col gap-2" style="max-height: 600px; overflow-y: auto;">
                           <div v-for="agent in agents" :key="agent.id"
                                class="flex items-center justify-between p-2 rounded cursor-pointer transition-all"
                                :class="activeAgentUUID === agent.id ? 'bg-primary-subtle border border-primary' : 'bg-light hover-bg-gray'"
                                @click="toggleAgentFilter(agent.id)" 
                                style="cursor: pointer;">
                               <div class="flex items-center gap-3">
                                   <div class="rounded-full flex items-center justify-center text-white" :class="agent.color" style="width: 32px; height: 32px; font-weight: bold; font-size: 0.8rem;">
                                       {{ agent.name.charAt(0).toUpperCase() }}
                                   </div>
                                   <div class="flex flex-col">
                                       <span class="font-bold text-dark small">{{ agent.name }}</span>
                                   </div>
                               </div>
                               <span class="badge bg-white text-secondary border rounded-pill px-2 small" v-if="activeAgentUUID === agent.id">Active</span>
                           </div>
                       </div>
                   </div>
               </div>
        </div>
    </div>
  </div>
</template>

<style scoped>
.dashboard-container {
    background-color: #f8f9fa;
    min-height: 100vh;
}

.header-subtitle { font-size: 12.5px; color: rgba(255,255,255,0.55); margin: 0; line-height: 1.4; }

.header-row {
    background-color: #1a1f36;
    padding: 1.5rem;
    border-radius: 8px;
    margin-left: -12px;
    margin-right: -12px;
    margin-top: -12px;
}

.chart-icon {
    font-size: 1.5rem;
}

.bg-primary-subtle {
    background-color: #e8f0fe;
}

.hover-bg-gray:hover {
    background-color: #e9ecef !important;
}

.transition-all {
    transition: all 0.2s ease;
}

.text-primary { color: #4285F4 !important; }
.text-success { color: #34A853 !important; }
.text-danger { color: #EA4335 !important; }

:deep(.date-picker-custom .dp__input:placeholder-shown) {
    text-indent: 20px !important;
}

:deep(.date-picker-custom .dp__input_icons) {
    padding: 0 !important;
}

.card-body {
  padding: 0 !important;
}

/* ── Dark mode ─────────────────────────────────────────────── */
[data-theme="dark"] .dashboard-container {
  background-color: transparent;
}

[data-theme="dark"] .card {
  border-color: #2d3f5a;
}

[data-theme="dark"] .card-title {
  color: #e8edf5;
}

[data-theme="dark"] .card-subtitle,
[data-theme="dark"] .text-gray-500 {
  color: #9aadc4 !important;
}

[data-theme="dark"] .text-success {
  color: #34d399 !important;
}

[data-theme="dark"] .text-danger {
  color: #ff8a8a !important;
}

[data-theme="dark"] .text-primary {
  color: #4e7fff !important;
}

/* Agent filter rows */
[data-theme="dark"] .bg-light {
  background-color: #28364e !important;
}

[data-theme="dark"] .text-dark {
  color: #e8edf5 !important;
}

[data-theme="dark"] .hover-bg-gray:hover {
  background-color: #3d5070 !important;
}

[data-theme="dark"] .bg-primary-subtle {
  background-color: rgba(78, 127, 255, 0.15) !important;
}

[data-theme="dark"] .border-primary {
  border-color: #4e7fff !important;
}

[data-theme="dark"] .badge.bg-white {
  background-color: #3d5070 !important;
  color: #9aadc4 !important;
  border-color: #4e7fff !important;
}

[data-theme="dark"] .text-secondary {
  color: #9aadc4 !important;
}

/* Maps table */
[data-theme="dark"] .table {
  color: #e8edf5;
  border-color: #2d3f5a;
}

[data-theme="dark"] .table thead.table-light th,
[data-theme="dark"] .table thead.table-light td {
  background-color: #161c2c !important;
  color: #9aadc4;
  border-color: #2d3f5a;
}

[data-theme="dark"] .table tbody tr td {
  border-color: #2d3f5a;
  color: #e8edf5;
}

[data-theme="dark"] .table-hover tbody tr:hover td {
  background-color: #28364e;
}
</style>
